'use client';

/**
 * Branded confirm() replacement.
 *
 * The browser-native `window.confirm()` looks alien on the rest of the app —
 * dark theme breaks, no translations, no styling control. This provider gives
 * a Promise-returning confirm with our design tokens.
 *
 * Usage in a component:
 *   const confirm = useConfirm();
 *   ...
 *   if (!(await confirm({
 *     title: 'Delete this prompt?',
 *     body: 'This cannot be undone.',
 *     confirmLabel: 'Delete',
 *     destructive: true,
 *   }))) return;
 *
 * Why custom (not sweetalert2):
 *   - Bundle: ~70KB saved
 *   - Theme: matches our --bg-raised / --line / --color-error tokens
 *   - i18n: copy can be passed by callers via useT() — no second locale system
 *
 * Behaviour:
 *   - Click backdrop or press Escape → resolves false (treated as cancel)
 *   - Click confirm button → resolves true
 *   - Stacks above other modals (z-index 90 — toast is 100, this is below
 *     so toasts stay visible after a confirm fires).
 *   - Body locks scroll while open — same trick as the existing modal.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  /** Button text — default "OK". */
  confirmLabel?: string;
  /** Button text — default "Cancel". */
  cancelLabel?: string;
  /** Make the confirm button red — for delete/destructive actions. */
  destructive?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return fn;
}

interface PendingDialog {
  opts: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingDialog | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setPending({ opts, resolve });
    });
  }, []);

  // Resolve + close. `value` is what the caller's await receives.
  const finish = useCallback((value: boolean) => {
    setPending((cur) => {
      if (cur) cur.resolve(value);
      return null;
    });
  }, []);

  // Escape closes as cancel.
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish(false);
      } else if (e.key === 'Enter') {
        // Enter = confirm — matches Swal's default. Avoids needing a mouse
        // for a yes/no question that just popped up under a click.
        e.preventDefault();
        finish(true);
      }
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while modal open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Auto-focus the confirm button (so Enter/Space immediately works).
    setTimeout(() => confirmBtnRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [pending, finish]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          // Backdrop
          onClick={() => finish(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 90,
            display: 'grid',
            placeItems: 'center',
            padding: 16,
            background: 'color-mix(in oklch, black 45%, transparent)',
            backdropFilter: 'blur(6px)',
            // Fade-in keeps it from feeling jarring.
            animation: 'ps-modal-fade 0.15s ease-out',
          }}
        >
          <div
            // Stop propagation so a click inside the card doesn't dismiss.
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-raised)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              padding: 24,
              width: 440,
              maxWidth: '100%',
              animation: 'ps-modal-slide 0.18s ease-out',
            }}
          >
            <div
              id="confirm-title"
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 20,
                fontWeight: 700,
                marginBottom: pending.opts.body ? 10 : 24,
                color: 'var(--ink)',
                lineHeight: 1.3,
              }}
            >
              {pending.opts.title}
            </div>
            {pending.opts.body && (
              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: 'var(--ink-2)',
                  marginBottom: 24,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {pending.opts.body}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={() => finish(false)}
                className="ps-btn ps-btn-ghost ps-btn-sm"
              >
                {pending.opts.cancelLabel || 'Cancel'}
              </button>
              <button
                ref={confirmBtnRef}
                type="button"
                onClick={() => finish(true)}
                className={`ps-btn ${pending.opts.destructive ? 'ps-btn-primary' : 'ps-btn-primary'} ps-btn-sm`}
                style={
                  pending.opts.destructive
                    ? { background: 'var(--color-error)', borderColor: 'var(--color-error)' }
                    : undefined
                }
              >
                {pending.opts.confirmLabel || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
